import type { InputHTMLAttributes } from 'react';

import { Input } from '../components/input';
import { cn } from '../lib/cn';

export type TotpCodeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'inputMode' | 'autoComplete' | 'value' | 'onChange'
> & {
  value: string;
  /** Receives digits only; everything else has already been stripped. */
  onValueChange: (value: string) => void;
  /** Called once `length` digits are present, so the form can submit itself. */
  onComplete?: (value: string) => void;
  /** 6 for TOTP; a backup code is longer, so the caller raises this. */
  length?: number;
};

/**
 * The one-time code field (M2.8, SECURITY.md §4.3).
 *
 * **One input, never six boxes.** Six boxes break paste, break the platform's own code
 * autofill, and are a well-known screen-reader hazard - the same decision the patient
 * app made for its email codes, kept here so staff and patients type a code the same
 * way. `autocomplete="one-time-code"` is what lets iOS and Android offer the code above
 * the keyboard; `inputmode="numeric"` raises the number pad without making the field
 * reject a pasted value.
 *
 * Filtering happens here rather than in every screen: a code pasted from an
 * authenticator app often arrives as "123 456", and a form that refuses it because of a
 * space is a form that blames the person for the app's formatting.
 *
 * **There is no `maxlength`, deliberately.** The browser applies it to the raw text
 * *before* any handler runs, so a pasted "123 456" would be truncated to "123 45" and
 * then filtered to five digits - the code silently wrong by one character, which is the
 * worst possible failure on this screen. The cap is applied after filtering instead, so
 * typing a seventh digit is still refused and a formatted paste still works.
 */
export function TotpCodeInput({
  value,
  onValueChange,
  onComplete,
  length = 6,
  className,
  ...rest
}: TotpCodeInputProps) {
  return (
    <Input
      {...rest}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      value={value}
      onChange={(event) => {
        const digits = event.target.value.replace(/\D/g, '').slice(0, length);
        onValueChange(digits);
        if (digits.length === length) onComplete?.(digits);
      }}
      className={cn('text-center text-2xl tracking-[8px] tabular-nums', className)}
    />
  );
}
